import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Tooltip } from 'antd';
import { CustomSelect } from 'components/molecules/PricingSimulator/DimSection';
import { timePeriodDropdownList } from 'components/molecules/TradePromoOptimization/PlanActions';
import PlanComparisonTableContainer from 'components/molecules/TradePromoOptimization/PlanComparisonTableContainer';
import { SettingOutlined, ProjectOutlined } from '@ant-design/icons';
import PlanComparisonSetupModal from 'components/molecules/TradePromoOptimization/Modals/PlanComparisonSetupModal';
import KPISelector from 'components/molecules/TradePromoOptimization/AddEventModal/PromoSections/KPISelector';
import { TPODataContext } from 'contexts/TPOContext';
import { tradePromoOptimizationUrls } from 'config/urls';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { queryClient } from 'services/reactQueryService';
import { planComparisonTableParser } from 'parser/tradePromoOptimization/plansParser';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig, transformFiltersV2, transformFiltersV3 } from 'utils/helperMethods';
import dayjs from 'dayjs';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import NewGlobalFilter from 'components/molecules/common/NewGlobalFilter/NewGlobalFilter';
import FilterRibbon from 'components/molecules/PricingSimulator/FilterRibbon/FilterRibbon';
import { DimensionDropdownType } from 'types/pricingSimulator';
import _ from 'lodash';
import { trackEventMixpanel } from 'utils/mixpanel';
import { TPO_PLAN_COMPARISON_RESULTS_VIEWED } from 'utils/mixpanelEventNames';

const PlanComparisonContainer = () => {
    const [selectedTimePeriod, setSelectedTimePeriod] = useState(timePeriodDropdownList?.[0]);
    const [openComparisonSetupModal, setOpenComparisonSetupModal] = useState(false);
    const [isKpiFilterVisible, setIsKpiFilterVisible] = useState(false);
    const [kpiFilterDropdownValues, setKpiDropdownValues] = useState<any[]>([]);
    const [selectedPlanIds, setSelectedPlanIds] = useState<number[]>([]);
    const [comparisonTableVariable, setComparisonTableVariable] = useState<any>({
        time_period: timePeriodDropdownList?.[0]?.value,
    });

    const filtersToDisplay = useRef<any>({});
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const tpoConfig = getConfig('tradePromoOptimization', appConfig);
    const { calendarFilterDimensionInfo, planMetricsFilterKeys } = tpoConfig;

    // Filter state variables
    const [selectedFilter, setSelectedFilter] = useState<any>(null);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<any>({});
    const [availableFilters, setAvailableFilters] = useState<DimensionDropdownType[]>(planMetricsFilterKeys);
    const [applyFilterObject, setApplyFilterObject] = useState<any>({});
    const [allCheckedAttribute, setAllCheckedAttribute] = useState<any>({});
    const [allSelectedValueForAllAttribute, setAllSelectedValueForAllAttribute] = useState<any>({});

    const { kpiConfigData, kpiRawData } = useContext(TPODataContext);

    useEffect(() => {
        const rawItems: any[] = kpiRawData ?? [];
        const filtered = rawItems.filter((item: any) => {
            const availableFor: string[] = item?.available_for || [];
            return availableFor.includes('comparison');
        });
        const dropdownList = filtered.map((item: any) => {
            return {
                label: item?.plan_label,
                value: item?.value,
                isSelected: item?.show_by_default || false,
            };
        });
        const selectedList = dropdownList.filter((item: any) => item?.isSelected).map((item: any) => item?.value);
        setKpiDropdownValues(dropdownList);
        setComparisonTableVariable({
            ...comparisonTableVariable,
            current_date: dayjs().unix(),
            kpi_list: selectedList,
        });
    }, [kpiRawData]);

    //comparison table data
    const {
        data: comparisonTableData,
        loading: comparisonTableDataLoading,
        error: comparisonTableDataError,
    } = useCommonApiHandler({
        method: 'post',
        queryConfig: {
            enabled: Boolean('plan_ids' in comparisonTableVariable && comparisonTableVariable.plan_ids?.length > 0),
        },
        queryKey: ['tpo-comparison-data-table', comparisonTableVariable],
        url: `${tradePromoOptimizationUrls.getPlansComparisonTableDataUrl}`,
        variables: { ...comparisonTableVariable },
        parserFunction: (res: any[]) => {
            if (res?.length === 0) {
                throw 'No Data';
            }
            const { result, exportData, exportHeader } = planComparisonTableParser(res, kpiConfigData, commonConfig);
            return { result, exportData, exportHeader };
        },
    });

    useEffect(() => {
        if (comparisonTableData?.result?.length) {
            trackEventMixpanel(TPO_PLAN_COMPARISON_RESULTS_VIEWED, {
                ...(filtersToDisplay.current ?? {}),
            });
        }
    }, [comparisonTableData?.result]);

    const resetFilterObject = () => {
        const allSelectedObj: any = {};
        const selectAllObj: any = {};
        const newApplyFilterObj: any = {};
        planMetricsFilterKeys.forEach((filter: any) => {
            allSelectedObj[filter.value] = new Set();
            newApplyFilterObj[filter.value] = {};
            selectAllObj[filter.value] = false;
        });
        setAvailableFilters(planMetricsFilterKeys);
        setAllCheckedAttribute(allSelectedObj);
        setApplyFilterObject(_.cloneDeep(newApplyFilterObj));
        setAllSelectedValueForAllAttribute(selectAllObj);
    };

    const onFilterSaveClickHandler = (resultObj: any) => {
        setAllCheckedAttribute(resultObj.allCheckedAttribute);
        setAllSelectedValueForAllAttribute(resultObj.allSelectedValueForAllAttribute);
        setApplyFilterObject(resultObj.applyFilterObject);

        const updatedFilters = transformFiltersV2(resultObj.applyFilterObject, planMetricsFilterKeys);
        setComparisonTableVariable({
            ...comparisonTableVariable,
            current_date: dayjs().unix(),
            filters: updatedFilters || {},
        });
    };

    const filterCommonVariables = useMemo(() => {
        if (selectedFilter?.value) {
            const updatedFilters = transformFiltersV3(
                tempApplyFilterObject,
                planMetricsFilterKeys,
                selectedFilter.value,
            );

            return {
                attribute_type: selectedFilter.value,
                limit: 50,
                filters: updatedFilters,
                plan_id: selectedPlanIds.length > 0 ? selectedPlanIds[0] : 0,
                is_tpm_plan: false,
                is_tpm_tab: false,
            };
        }
    }, [selectedFilter, selectedPlanIds]);

    useEffect(() => {
        resetFilterObject();
        setSelectedPlanIds([]);
    }, []);

    const hasNoBasePlanSelected = useMemo(() => {
        return selectedPlanIds.length === 0 || false;
    }, [selectedPlanIds]);

    const titleArray = useMemo(() => {
        return [
            ['Filtered by:'],
            ...Object.keys(filtersToDisplay.current ?? {}).map((item: any) => {
                return [`${item}: ${filtersToDisplay.current[item]}`];
            }),
            [''],
        ];
    }, [selectedTimePeriod, selectedPlanIds]);
    return (
        <div>
            <div className="flex mt-12 justify-between items-end">
                <div className="flex gap-4 items-center">
                    <Button
                        onClick={() => {
                            setOpenComparisonSetupModal(true);
                        }}
                        className="!text-[13px]"
                        style={{ borderRadius: 6 }}
                        icon={<SettingOutlined style={{ position: 'relative', top: -3 }} />}
                    >
                        Plan Comparison Setup
                    </Button>
                </div>
                <div className="flex items-end gap-2">
                    <div>
                        <p className="mb-1 font-xs  text-fontGreyAlt ">Time Period</p>
                        <CustomSelect
                            placeholder="Time Period"
                            className="dropdown-height dropdown-width "
                            showArrow
                            loading={false}
                            value={selectedTimePeriod?.value}
                            disabled={false}
                            options={timePeriodDropdownList}
                            onChange={(val: any, item: any) => {
                                setSelectedTimePeriod(item);
                                setComparisonTableVariable({
                                    ...comparisonTableVariable,
                                    current_date: dayjs().unix(),
                                    time_period: val,
                                });
                            }}
                        />
                    </div>
                    <Tooltip
                        title={hasNoBasePlanSelected ? 'Please select base plan to apply filters' : ''}
                        placement="bottom"
                    >
                        <span>
                            <NewGlobalFilter
                                disabled={hasNoBasePlanSelected}
                                availableFilters={availableFilters}
                                allCheckedAttribute={allCheckedAttribute}
                                allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                                applyFilterObject={applyFilterObject}
                                resetFilterObject={resetFilterObject}
                                onSubmitHandler={onFilterSaveClickHandler}
                                url={tradePromoOptimizationUrls.getCalendarDataFilterAttributes}
                                selectedFilter={selectedFilter}
                                setSelectedFilter={setSelectedFilter}
                                tempApplyFilterObject={tempApplyFilterObject}
                                setTempApplyFilterObject={setTempApplyFilterObject}
                                apiVariables={filterCommonVariables}
                                filterDimensionInfo={calendarFilterDimensionInfo}
                                showAttributePanelToLeft
                            />
                        </span>
                    </Tooltip>
                    <KPISelector
                        buttonComponent={
                            <Button
                                onClick={() => {
                                    setIsKpiFilterVisible(!isKpiFilterVisible);
                                }}
                                icon={<ProjectOutlined style={{ position: 'relative', top: -3 }} />}
                                style={{ borderRadius: 4, margin: 0 }}
                                type="default"
                            >
                                KPI
                            </Button>
                        }
                        isVisible={isKpiFilterVisible}
                        setIsVisible={setIsKpiFilterVisible}
                        kpiList={kpiFilterDropdownValues}
                        onSubmit={(values) => {
                            const updatedList = kpiFilterDropdownValues?.map((item) => {
                                return {
                                    ...item,
                                    isSelected: values.includes(item?.value),
                                };
                            });
                            setComparisonTableVariable({
                                ...comparisonTableVariable,
                                current_date: dayjs().unix(),
                                kpi_list: values,
                            });
                            setKpiDropdownValues(updatedList);
                        }}
                        variableName={'KPI'}
                        hideSearch={true}
                    />
                </div>
            </div>
            <div className="my-2 ">
                <FilterRibbon
                    allCheckedAttribute={allCheckedAttribute}
                    allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                    applyFilterObject={applyFilterObject}
                    resetFilterObject={resetFilterObject}
                    onSubmitHandler={onFilterSaveClickHandler}
                />
            </div>
            <div className="flex items-center justify-between my-2">
                <div className="flex items-center font-xs text-fontGreyAlt gap-4">
                    {Object.keys(filtersToDisplay.current ?? {}).map((item: any) => {
                        const value = filtersToDisplay.current[item];
                        const displayValue = Array.isArray(value) ? value.join(', ') : value;
                        return (
                            <p key={item}>
                                {item}: {displayValue}
                            </p>
                        );
                    })}
                </div>
                {comparisonTableData?.exportData && (
                    <DownloadExcel
                        excludeFilters={true}
                        title={'PlanComparisonExportData'}
                        titleArray={titleArray}
                        excelData={comparisonTableData?.exportData ?? []}
                        headers={comparisonTableData?.exportHeader ?? []}
                    />
                )}
            </div>

            <PlanComparisonTableContainer
                arePlansSelected={selectedPlanIds.length > 0}
                data={comparisonTableData?.result}
                loading={comparisonTableDataLoading}
                error={comparisonTableDataError}
            />
            <PlanComparisonSetupModal
                applyComparisonSetupLoading={comparisonTableDataLoading}
                disabled={false}
                open={openComparisonSetupModal}
                onCancel={() => {
                    setOpenComparisonSetupModal(false);
                }}
                data={{ selectedPlanIds }}
                onSubmitFinish={(plansList: number[], filters: any) => {
                    queryClient.removeQueries({
                        queryKey: ['tpo-comparison-data-table'],
                    });
                    filtersToDisplay.current = filters;
                    setSelectedPlanIds(plansList);
                    setComparisonTableVariable({
                        ...comparisonTableVariable,
                        current_date: dayjs().unix(),
                        plan_ids: plansList,
                    });
                    resetFilterObject();
                    setOpenComparisonSetupModal(false);
                }}
            />
        </div>
    );
};

export default PlanComparisonContainer;
